'use client';

import { logger } from '@/utils/loggerUtils';
import { useLocale } from '@vimond/i18n/hooks/useLocale';
import ar from '@vimond/i18n/locales/ar.json';
import bn from '@vimond/i18n/locales/bn.json';
import cs from '@vimond/i18n/locales/cs.json';
import da from '@vimond/i18n/locales/da.json';
import de from '@vimond/i18n/locales/de.json';
import el from '@vimond/i18n/locales/el.json';
import en from '@vimond/i18n/locales/en.json';
import es from '@vimond/i18n/locales/es.json';
import et from '@vimond/i18n/locales/et.json';
import fi from '@vimond/i18n/locales/fi.json';
import fr from '@vimond/i18n/locales/fr.json';
import hu from '@vimond/i18n/locales/hu.json';
import is from '@vimond/i18n/locales/is.json';
import it from '@vimond/i18n/locales/it.json';
import ja from '@vimond/i18n/locales/ja.json';
import lt from '@vimond/i18n/locales/lt.json';
import lv from '@vimond/i18n/locales/lv.json';
import nb from '@vimond/i18n/locales/nb.json';
import nl from '@vimond/i18n/locales/nl.json';
import no from '@vimond/i18n/locales/no.json';
import pl from '@vimond/i18n/locales/pl.json';
import pt from '@vimond/i18n/locales/pt.json';
import ro from '@vimond/i18n/locales/ro.json';
import ru from '@vimond/i18n/locales/ru.json';
import sk from '@vimond/i18n/locales/sk.json';
import sr from '@vimond/i18n/locales/sr.json';
import sv from '@vimond/i18n/locales/sv.json';
import ta from '@vimond/i18n/locales/ta.json';
import tr from '@vimond/i18n/locales/tr.json';
import ur from '@vimond/i18n/locales/ur.json';
import yue from '@vimond/i18n/locales/yue.json';
import zg from '@vimond/i18n/locales/zg.json';
import {
  default as zh,
  default as zh_cn,
} from '@vimond/i18n/locales/zh_cn.json';
import zh_tw from '@vimond/i18n/locales/zh_tw.json';
import { cookieName, getOptions, languages } from '@vimond/i18n/settings';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import {
  initReactI18next,
  useTranslation as useTranslationOrg,
} from 'react-i18next';

const runsOnServerSide = typeof window === 'undefined';

// on client side the normal singleton is ok
i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['path', 'htmlTag', 'cookie', 'navigator'],
    },
    preload: runsOnServerSide ? languages : [],
    resources: {
      ar,
      bn,
      cs,
      da,
      de,
      el,
      en,
      es,
      et,
      fi,
      fr,
      hu,
      is,
      it,
      ja,
      lt,
      lv,
      nb,
      nl,
      no,
      pl,
      pt,
      ro,
      ru,
      sk,
      sr,
      sv,
      ta,
      tr,
      ur,
      yue,
      zg,
      zh,
      zh_cn,
      zh_tw,
    },
  })
  .catch((e) => {
    logger.error('Error initializing i18n', e);
  });

export function useTranslation(ns?: string) {
  const router = useRouter();
  const { locale } = useLocale();
  const [cookies, setCookie] = useCookies([cookieName]);
  const translation = useTranslationOrg(ns);
  const { i18n } = translation;
  const [activeLanguage, setActiveLanguage] = useState(i18n.resolvedLanguage);

  // Sync language with i18n on server side
  if (runsOnServerSide && locale && i18n.resolvedLanguage !== locale) {
    void i18n.changeLanguage(locale);
  }

  // Sync language and cookies on client side
  useEffect(() => {
    if (activeLanguage !== i18n.resolvedLanguage) {
      setActiveLanguage(i18n.resolvedLanguage);
    }
  }, [activeLanguage, i18n.resolvedLanguage]);

  useEffect(() => {
    if (
      locale &&
      i18n.resolvedLanguage &&
      i18n.resolvedLanguage !== locale &&
      !(
        locale.startsWith(i18n.resolvedLanguage) ||
        i18n.resolvedLanguage?.startsWith(locale)
      )
    ) {
      void i18n.changeLanguage(locale);
      router.refresh(); // Refresh is required for server-side content to pick up the cookie change.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, i18n]);

  useEffect(() => {
    if (locale && cookies['Next-Locale'] !== locale) {
      setCookie(cookieName, locale, { path: '/' });
    }
  }, [locale, cookies, setCookie]);

  return translation;
}
